import { useEffect, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import {
  getTokenOrSignOut,
  setAuthTokenGetter,
  setLogoutHandler,
} from '@/store/api/baseApi';

// A copy kept for developer tools; failing to store it must not stop the app from loading
const storeDevCopy = (token: string) => {
  try {
    sessionStorage.setItem('accessToken-dev', token);
  } catch (err) {
    console.error('Could not store the dev copy of the access token', err);
  }
};

/**
 * Gets the app's first access token. A rejected refresh token keeps loading while the sign-out
 * leaves the page. A request that failed for a reason that may pass ends loading without a token,
 * and so does a sign-out that could not leave the page.
 */
export const useToken = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } =
    useAuth0();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isTokenLoading, setIsTokenLoading] = useState(true);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    // Set up the RTK Query token getter and logout handler first: the first token comes through
    // them, so a session Auth0 has ended signs the user out here as it would on any request
    setAuthTokenGetter(() => getAccessTokenSilently());
    setLogoutHandler(() => {
      logout({ logoutParams: { returnTo: globalThis.location.origin } }).catch(
        err => {
          // The page is not leaving, so the loading screen would never end; show the error page
          console.error('Could not sign out', err);
          setIsTokenLoading(false);
        }
      );
    });

    getTokenOrSignOut().then(result => {
      // The sign-out is under way, so stay on the loading screen until the page leaves
      if (result.kind === 'sessionEnded') return;

      if (result.kind === 'token') {
        setAccessToken(result.token);
        storeDevCopy(result.token);
      }
      setIsTokenLoading(false);
    });
  }, [isAuthenticated, isLoading, getAccessTokenSilently, logout]);

  return { accessToken, isTokenLoading };
};
